import { TokenKind } from '../../json/tokens';
import { scanStructure, structuralTokens, textOf } from '../support';
import type { RepairContext, RepairProposal, RepairRule } from '../types';

/**
 * `undefined` → `null`.
 *
 * JSON has no `undefined`, and the one thing a JSON document can say about "no value" is
 * `null`, so the substitution is not a guess about intent — it is the only representable
 * meaning. The legacy fixer listed `undefined` in the skip-list of its unquoted-value rule
 * and therefore left the document unparseable while reporting nothing (recon B18).
 *
 * Key position is excluded: `{undefined:1}` is a bare property name, which is
 * `quote-unquoted-key`'s business, and firing both would put two rules on one span.
 */
export const normalizeJsLiteralRule: RepairRule = {
  id: 'normalize-js-literal',
  confidence: 'safe',
  priority: 34,
  resolves: ['literal/js-undefined'],

  detect(ctx: RepairContext): readonly RepairProposal[] {
    const { namedKeys } = scanStructure(ctx.source, ctx.tokens);
    const proposals: RepairProposal[] = [];

    for (const token of structuralTokens(ctx.source, ctx.tokens)) {
      if (token.kind !== TokenKind.Unknown) continue;
      // Only a token the grammar reads as a key AND that is followed by a colon is
      // provably a property name; that is the one quote-unquoted-key claims. Anything
      // looser would skip the `Infinity` of `-Infinity`, which recovery also parks in key
      // position.
      if (namedKeys.has(token)) continue;
      if (textOf(ctx.source, token) !== 'undefined') continue;

      proposals.push({
        ruleId: 'normalize-js-literal',
        span: { from: token.from, to: token.to },
        edits: [{ from: token.from, to: token.to, insert: 'null' }],
        labelCode: 'fix.normalize-js-literal',
        labelParams: { literal: 'undefined' },
        resolves: 'literal/js-undefined',
      });
    }

    return proposals;
  },
};
